import {
	DateString,
	Highlight,
	HighlightedText,
	ListItem,
	Typography
} from "client/Components";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNil,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { IconThumbnail } from "./IconThumbnail";
import { HighlightItem } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { useHighlights } from "../ResultView/CategoryView/ItemView/useHighlights";
import { ReactElement } from "react";
import { SubTitleSection } from "./SubTitleSection";
import { TimeZone } from "client/DateAndTime/timezones";
import styles from "./EmailListItem.module.less";
import { useGlobalSearchItemActions } from "../GlobalSearchItem/useGlobalSearchItemActions";
import { AppColorViewModel } from "client/Api/Apps/common";

export type EmailListItemProps = {
	emailId: string;
	subject: string | null;
	ownerName: string | null;
	receivedDateTime: string | null;
	parentFolderName: string | null;
	color: AppColorViewModel;
	hitHighlights?: readonly HighlightItem[];
	timezone: TimeZone;
	onRemove?: () => void;
	itemIndex: number;
	tabIndex: number;
	onExecute: () => void;
};

export const EmailListItem = (props: EmailListItemProps): ReactElement => {
	const {
		emailId,
		subject,
		ownerName,
		receivedDateTime,
		parentFolderName,
		color,
		hitHighlights = EMPTY_ARRAY,
		timezone,
		onRemove,
		itemIndex,
		tabIndex,
		onExecute
	} = props;

	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex,
		onExecute
	});

	return (
		<ListItem
			trackingId={generateTrackingId("email-result-list-item", `|${emailId}`)}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<IconThumbnail
						icon="envelope"
						color={color}
						trackingId="thumbnail"
					/>
				}
			>
				<ListItem.Title>
					<Typography
						weight="bold"
						trackingId="title"
					>
						{isNotNilOrEmpty(subject)
							? (
								<HighlightedText
									text={subject}
									highlight={highlights.get("item.subject")}
								/>
							)
							: localize("_NoSubject")
						}
					</Typography>
				</ListItem.Title>
				<ListItem.SubTitle>
					<SubTitleSection
						trackingId="owner"
						text={(
							<HighlightedText
								text={ownerName}
								highlight={highlights.get("item.ownerName")}
							/>
						)}
						isEmpty={!ownerName}
					/>
					<SubTitleSection
						trackingId="timestamp"
						text={(
							<>
								{receivedDateTime && (
									<DateString
										value={receivedDateTime}
										format="AbsoluteTimestamp"
										timezone={timezone}
									/>
								)}
							</>
						)}
						isEmpty={!receivedDateTime}
					/>
					<SubTitleSection
						trackingId="parent-folder"
						text={(
							<span className={styles.folderName}>
								{parentFolderName}
							</span>
						)}
						isEmpty={!parentFolderName}
					/>
				</ListItem.SubTitle>
			</ListItem.Content>
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
EmailListItem.displayName = "EmailListItem";